import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import { FaTimes } from 'react-icons/fa'
import CreatableSelect from 'react-select/creatable'
import { useNavigate, useParams } from 'react-router-dom'
import useFetch from '../../../../_fetch'
import { IPartnership, PartnershipType } from '../types'

function getPayoutLabel(partnership: IPartnership) {
    if (partnership.type === 'sale') return `${partnership.value}% of sale`
    if (partnership.type === 'click') return `${partnership.value} ${partnership.currency} per click`
    return `${partnership.value} ${partnership.currency} per lead`
}

const PartnershipDetail: React.FC = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { doFetch } = useFetch()

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [domainError, setDomainError] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [partnership, setPartnership] = useState<IPartnership | null>(null)
    const [formDomains, setFormDomains] = useState<string[]>([])

    const [form, setForm] = useState({
        name: '',
        type: 'sale' as PartnershipType,
        value: '',
        currency: 'DKK',
        startDate: '',
        expiryDate: '',
        cookieDays: '30',
    })

    useEffect(() => {
        const fetchPartnership = async () => {
            try {
                setLoading(true)
                setError('')

                const res = await doFetch(`/byelink/partnerships/${id}`)
                if (res.status >= 400) throw await res.text()

                const data = await res.json()
                setPartnership(data)
                setForm({
                    name: data.name || '',
                    type: data.type || 'sale',
                    value: String(data.value ?? ''),
                    currency: data.currency || 'DKK',
                    startDate: data.startDate || '',
                    expiryDate: data.expiryDate || '',
                    cookieDays: String(data.cookieDays ?? 30),
                })
                setFormDomains(Array.isArray(data.domains) ? data.domains : [])
            } catch (err: any) {
                setError(typeof err === 'string' ? err : 'Could not load partnership')
            } finally {
                setLoading(false)
            }
        }

        if (id) fetchPartnership()
    }, [doFetch, id])

    const entryScript = useMemo(() => `<script>

</script>`, [])
    const conversionScript = useMemo(() => `<script>

</script>`, [])

    const updateForm = (key: keyof typeof form, value: string) => {
        setForm((prev) => ({ ...prev, [key]: value }))
    }

    const stripCommonSubdomain = (hostname: string) => hostname.replace(/^www\d*\./i, '')

    const cleanDomain = (input: string) => {
        try {
            const url = input.startsWith('http') ? input : `https://${input}`
            const parsed = new URL(url)
            return stripCommonSubdomain(parsed.hostname.toLowerCase())
        } catch {
            return null
        }
    }

    const isValidDomain = (domain: string) => /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)

    const addDomain = (option: any) => {
        const raw = option?.value?.trim()?.toLowerCase()
        if (!raw) return

        const domain = cleanDomain(raw)
        if (!domain || !isValidDomain(domain)) {
            setDomainError('Invalid domain')
            return
        }

        setDomainError('')
        setFormDomains((current) => (current.includes(domain) ? current : [...current, domain]))
    }

    const removeDomain = (domain: string) => {
        if (!isEditing) return
        setFormDomains((current) => current.filter((entry) => entry !== domain))
    }

    const handleStartEdit = () => {
        setIsEditing(true)
        setError('')
        setSuccess('')
    }

    const handleCancelEdit = () => {
        if (!partnership) return

        setForm({
            name: partnership.name || '',
            type: partnership.type || 'sale',
            value: String(partnership.value ?? ''),
            currency: partnership.currency || 'DKK',
            startDate: partnership.startDate || '',
            expiryDate: partnership.expiryDate || '',
            cookieDays: String(partnership.cookieDays ?? 30),
        })
        setFormDomains(Array.isArray(partnership.domains) ? partnership.domains : [])
        setDomainError('')
        setError('')
        setSuccess('')
        setIsEditing(false)
    }

    const handleCopy = async (value: string) => {
        await navigator.clipboard.writeText(value)
        setSuccess('Script copied')
        setTimeout(() => setSuccess(''), 1500)
    }

    const handleSave = async () => {
        if (!partnership) return

        setError('')
        setSuccess('')

        if (!form.name.trim()) {
            setError('Partner name is required')
            return
        }

        if (!form.startDate) {
            setError('Start date is required')
            return
        }

        if (!formDomains.length) {
            setError('At least one domain is required')
            return
        }

        const valueNumber = Number(form.value)
        if (!Number.isFinite(valueNumber) || valueNumber < 0) {
            setError('Value must be a positive number')
            return
        }

        const cookieDays = Number(form.cookieDays)
        if (!Number.isInteger(cookieDays) || cookieDays <= 0) {
            setError('Cookie duration must be at least 1 day')
            return
        }

        try {
            setSaving(true)

            const payload = {
                id: partnership.id,
                name: form.name,
                domains: formDomains,
                type: form.type,
                value: valueNumber,
                currency: form.currency,
                startDate: form.startDate,
                expiryDate: form.expiryDate || null,
                cookieDays,
            }

            const res = await doFetch('/byelink/partnerships/update', payload)
            if (res.status >= 400) throw await res.text()

            const updated = await res.json()
            setPartnership(updated)
            setSuccess('Partnership saved')
            setIsEditing(false)
        } catch (err: any) {
            setError(typeof err === 'string' ? err : 'Could not save partnership')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!partnership) return

        try {
            setDeleting(true)
            setError('')
            setSuccess('')

            const res = await doFetch('/byelink/partnerships/delete', { id: partnership.id })
            if (res.status >= 400) throw await res.text()

            navigate('/link-script/partnerships')
        } catch (err: any) {
            setError(typeof err === 'string' ? err : 'Could not delete partnership')
        } finally {
            setDeleting(false)
            setShowDeleteModal(false)
        }
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" />
            </div>
        )
    }

    if (!partnership) {
        return <Alert variant="warning">Partnership was not found.</Alert>
    }

    return (
        <div className="pe-2">
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Card className="mb-3">
                <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                    <div>
                        <div className="text-muted small">Partnership</div>
                        <h5 className="mb-0">{partnership.name}</h5>
                    </div>
                    <div className="d-flex align-items-center">
                        <Button variant="outline-secondary" onClick={() => navigate('/link-script/partnerships')}>
                            Back to list
                        </Button>
                        {!isEditing ? (
                            <Button variant="primary" className="ms-3" onClick={handleStartEdit}>
                                Edit
                            </Button>
                        ) : (
                            <>
                                <Button variant="outline-secondary" className="ms-3" onClick={handleCancelEdit}>
                                    Cancel
                                </Button>
                                <Button variant="outline-danger" className="ms-2" onClick={() => setShowDeleteModal(true)}>
                                    Delete
                                </Button>
                            </>
                        )}
                    </div>
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={6}><strong>Type:</strong> <Badge bg="secondary">{partnership.type}</Badge></Col>
                        <Col md={6}><strong>Payout:</strong> {getPayoutLabel(partnership)}</Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Partner name</Form.Label>
                                <Form.Control
                                    value={form.name}
                                    disabled={!isEditing}
                                    onChange={(event) => updateForm('name', event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Partner domains</Form.Label>
                                <CreatableSelect
                                    placeholder="Add domain (e.g. partner.com)"
                                    isClearable
                                    isDisabled={!isEditing}
                                    formatCreateLabel={(value) => `Add "${value}"`}
                                    onChange={isEditing ? addDomain : undefined}
                                    onCreateOption={isEditing ? (value) => addDomain({ value }) : undefined}
                                />
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                    {formDomains.map((domain) => (
                                        <Badge key={domain} bg="info" className="d-flex align-items-center px-2 py-1">
                                            {domain}
                                            <FaTimes
                                                className="ms-2"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => removeDomain(domain)}
                                            />
                                        </Badge>
                                    ))}
                                </div>
                                {domainError && <div className="text-danger small mt-2">{domainError}</div>}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Type</Form.Label>
                                <Form.Select
                                    value={form.type}
                                    disabled={!isEditing}
                                    onChange={(event) => updateForm('type', event.target.value as PartnershipType)}
                                >
                                    <option value="sale">Sale</option>
                                    <option value="click">Click</option>
                                    <option value="lead">Lead</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Value</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={form.value}
                                    disabled={!isEditing}
                                    onChange={(event) => updateForm('value', event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Currency</Form.Label>
                                <Form.Control
                                    value={form.currency}
                                    disabled={!isEditing}
                                    onChange={(event) => updateForm('currency', event.target.value.toUpperCase())}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Start date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={form.startDate}
                                    disabled={!isEditing}
                                    onChange={(event) => updateForm('startDate', event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>End date (optional)</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={form.expiryDate}
                                    disabled={!isEditing}
                                    onChange={(event) => updateForm('expiryDate', event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Cookie duration (days)</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={1}
                                    value={form.cookieDays}
                                    disabled={!isEditing}
                                    onChange={(event) => updateForm('cookieDays', event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {isEditing && (
                        <div className="d-flex justify-content-end">
                            <Button onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving...' : 'Save changes'}
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>Script 1: Entry tracker</strong>
                    <Button size="sm" variant="outline-secondary" onClick={() => handleCopy(entryScript)}>Copy</Button>
                </Card.Header>
                <Card.Body>
                    <p className="mb-2 text-muted">Placeholder block. Script content will be added later.</p>
                    <pre className="mb-0 p-3" style={{ background: '#f7f7f7', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>
                        {entryScript}
                    </pre>
                </Card.Body>
            </Card>

            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>Script 2: Conversion tracker</strong>
                    <Button size="sm" variant="outline-secondary" onClick={() => handleCopy(conversionScript)}>Copy</Button>
                </Card.Header>
                <Card.Body>
                    <p className="mb-2 text-muted">Placeholder block. Script content will be added later.</p>
                    <pre className="mb-0 p-3" style={{ background: '#f7f7f7', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>
                        {conversionScript}
                    </pre>
                </Card.Body>
            </Card>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete partnership</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    This will permanently delete <strong>{partnership.name}</strong>. This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete partnership'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default PartnershipDetail
